import React from 'react';
import {Link} from 'react-router-dom';
import styles from '../Home.module.css';

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;

const iconMaskStyle = (url: string) => ({
  '--footer-icon-url': `url("${url}")`,
}) as React.CSSProperties;

type FooterLink = {
  label: string;
  to: string;
  icon: string;
};

const navigationLinks = [
  {label: 'Home', to: '/home', icon: 'home-fill.svg'},
  {label: 'Services', to: '/services', icon: 'service-fill.svg'},
  {label: 'Profile', to: '/profile', icon: 'profile-fill.svg'},
  {label: 'Messages', to: '/messages', icon: 'message-fill.svg'},
  {label: 'Analytics', to: '/analytics', icon: 'analytics-fill.svg'},
  {label: 'Settings', to: '/settings', icon: 'setting-fill.svg'},
] satisfies FooterLink[];

const exploreLinks = [
  {label: 'Live Now', to: '/home#live-now', icon: 'live-fill.svg'},
  {label: 'Reels', to: '/home#reels', icon: 'video-two.svg'},
  {label: 'Recent Posts', to: '/home#recent-posts', icon: 'post-fill.svg'},
  {label: 'Discover Talent', to: '/discover-talent', icon: 'search-line.svg'},
  {label: 'Trending Topics', to: '/home#trending-topics', icon: 'trend-up-fill.svg'},
] satisfies FooterLink[];

const socialLinks = [
  {label: 'Instagram', icon: 'logos-instagram.svg'},
  {label: 'TikTok', icon: 'logos-tiktok.svg'},
  {label: 'YouTube', icon: 'logos-youtube.svg'},
  {label: 'Twitter', icon: 'logos-twitter.svg'},
  {label: 'Facebook', icon: 'logos-facebook.svg'},
  {label: 'LinkedIn', icon: 'logos-linkedin.svg'},
];

const renderIcon = (icon: string) => (
  <span
    className={styles.platformFooterLinkIcon}
    style={iconMaskStyle(iconUrl(icon))}
    aria-hidden="true"
  />
);

const handlePrototypeSubscribe = (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  window.alert('This feature will come soon.');
};

const HomeFooter: React.FC = () => (
  <footer className={styles.platformFooter}>
    <div className={styles.platformFooterInner}>
      <div className={styles.platformFooterTop}>
        <section className={styles.platformFooterBrand} aria-label="XPSR footer introduction">
          <div className={styles.platformFooterBrandHeader}>
            <img
              src={`${process.env.PUBLIC_URL}/xpsr-logo.png`}
              alt="XPSR"
              className={styles.platformFooterLogo}
              loading="lazy"
            />
            <div>
              <p className={styles.platformFooterBrandName}>XPSR</p>
              <p className={styles.platformFooterBrandTagline}>Express Yourself</p>
            </div>
          </div>
          <p className={styles.platformFooterDescription}>
            The social platform where creators connect, share, and inspire.
          </p>
          <div className={styles.platformFooterSocials} aria-label="Social links">
            {socialLinks.map((social) => (
              <button key={social.label} type="button" className={styles.platformFooterSocialButton} aria-label={social.label}>
                <img
                  src={iconUrl(social.icon)}
                  alt=""
                  className={styles.platformFooterSocialLogo}
                  aria-hidden="true"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </section>

        <nav className={styles.platformFooterColumn} aria-label="Footer navigation">
          <h2 className={styles.platformFooterTitle}>Navigation</h2>
          <ul className={styles.platformFooterLinkList}>
            {navigationLinks.map((link) => (
              <li key={link.label}>
                <Link className={styles.platformFooterLink} to={link.to}>
                  {renderIcon(link.icon)}
                  <span>{link.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className={styles.platformFooterColumn} aria-label="Footer explore links">
          <h2 className={styles.platformFooterTitle}>Explore</h2>
          <ul className={styles.platformFooterLinkList}>
            {exploreLinks.map((link) => (
              <li key={link.label}>
                <Link className={styles.platformFooterLink} to={link.to}>
                  {renderIcon(link.icon)}
                  <span>{link.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <section className={`${styles.platformFooterColumn} ${styles.platformFooterNewsletter}`} aria-labelledby="platform-footer-newsletter-title">
          <h2 id="platform-footer-newsletter-title" className={styles.platformFooterTitle}>Stay in the loop</h2>
          <p className={styles.platformFooterNewsletterText}>
            Get the latest updates, features, and creator tips straight to your inbox.
          </p>
          <form className={styles.platformFooterNewsletterForm} onSubmit={handlePrototypeSubscribe} noValidate>
            <label className={styles.platformFooterEmailLabel}>
              <span className={styles.platformFooterSrOnly}>Email address</span>
              <input
                className={styles.platformFooterEmailInput}
                type="email"
                name="email"
                placeholder="Enter your email address"
                autoComplete="email"
              />
            </label>
            <button type="submit" className={styles.platformFooterSubscribeButton}>Subscribe</button>
          </form>
        </section>
      </div>

      <div className={styles.platformFooterBottom}>
        <p className={styles.platformFooterCopyright}>&copy; 2026 XPSR. All rights reserved.</p>
        <nav className={styles.platformFooterLegalLinks} aria-label="Footer legal links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </div>
    </div>
  </footer>
);

export default React.memo(HomeFooter);
